/** Diagnostics messages bypass application RPC queues. Never carry SQL, bodies or credentials. */
export const RUNTIME_PROBE = 'runtime:probe'
export const RUNTIME_REPLY = 'runtime:reply'

export type OperationKind = 'startup' | 'navigation' | 'database' | 'task' | 'network' | 'storage'
export type OperationStatus = 'queued' | 'running' | 'waiting' | 'completed' | 'failed'
export interface RuntimeOperation {
  id: string
  name: string
  kind: OperationKind
  status: OperationStatus
  startedAt: number
  endedAt?: number
  parentId?: string
  resourceId?: string
  waitingFor?: string
  detail?: string
  error?: string
}
export type WorkerStatus = 'unknown' | 'checking' | 'responsive' | 'unresponsive' | 'failed' | 'closed'
export interface RuntimeWorker {
  id: string
  name: string
  kind: 'dedicated' | 'shared' | 'service'
  parentId?: string
  status: WorkerStatus
  startedAt: number
  lastResponseAt?: number
  checkedAt?: number
  error?: string
}
export interface WorkerSnapshot {
  operations: RuntimeOperation[]
  workers: RuntimeWorker[]
}
export interface RuntimeEndpoint {
  postMessage(message: unknown): void
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void
}

export function isRuntimeReply(value: unknown): value is { type: typeof RUNTIME_REPLY; id: string; snapshot: WorkerSnapshot } {
  if (!value || typeof value !== 'object') return false
  const candidate = value as { type?: unknown; id?: unknown; snapshot?: WorkerSnapshot }
  return candidate.type === RUNTIME_REPLY && typeof candidate.id === 'string' &&
    Array.isArray(candidate.snapshot?.operations) && Array.isArray(candidate.snapshot.workers)
}

/** Keep query strings, credentials and fragments out of diagnostic reports. */
export function runtimeUrl(input: string): string {
  try {
    const url = new URL(input, 'https://relative.invalid')
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return url.protocol
    return `${url.origin === 'https://relative.invalid' ? '' : url.origin}${url.pathname}`
  } catch { return 'Invalid URL' }
}

export function runtimeError(error: unknown): string {
  // Error messages can contain tokens, SQL values or user content. Export only the type.
  return error instanceof Error ? error.name : 'Unknown error'
}
